import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

import { Position } from 'geojson';

import { GeoSearchModule } from '@motes-sdk/components';
import { Coords, fileToDataUrl, Media, Mote } from '@motes-sdk/models';
import { AddressInfo, AutoSub, AutoSubOnDestroy, MapService, MoteService, NewStoryInfo, RestService } from '@motes-sdk/providers';
import { GeoSearchComponent } from '@motes-sdk/components/geo-search/geo-search.component';
import { GeoSearchService, PlaceResult } from '@motes-sdk/components/geo-search/geo-search.service';
import { Animations } from '@motes-sdk/animations';

import { MapEditorService } from '../../../../services';
import { MapEditorEventType, MoveStoryEvent } from '../../../../models/map-editor-event';
import { MapOverlayService } from '../../../../services/map-overlay.service';

@AutoSubOnDestroy()
@Component({
    selector: 'map-search-bar',
    standalone: true,
    imports: [CommonModule, GeoSearchModule],
    templateUrl: './map-search-bar.component.html',
    styleUrls: ['./map-search-bar.component.scss'],
    animations: [Animations.fadeInOut(300, 200)],
    encapsulation: ViewEncapsulation.None
})
export class MapSearchBarComponent implements OnInit, OnDestroy {

    @ViewChild('geoSearch', { static: true }) geoSearch: GeoSearchComponent;

    @Input('activeStoryKey') set activeStoryKey(activeStoryKey: string) {
        // If relocation is underway and user opens story pane => reset relocation
        if(activeStoryKey && this.storyRelocated) this.restoreStoryPosition();
        // Otherwise, if user relocates while story pane is open,
        // or redirects away from map while relocation is not underway => close and clear search bar
        else if(activeStoryKey || !this.storyRelocated) this.geoSearch.closeAndClear();
    }

    @Output() addStory = new EventEmitter<NewStoryInfo>();
    @Output() updateStoryImage = new EventEmitter<{ story: Mote, image: File }>();
    @Output() navigateToStory = new EventEmitter<Mote>();

    public searchProximityCoords: Coords;
    public searchResult: PlaceResult;
    public storyRelocated: MoveStoryEvent;
    public relocatedStory: Mote;
    public storyImage: string;
    public streetViewImageFile: File;

    constructor(
        private mapService: MapService,
        private mapEditorService: MapEditorService,
        private geoSearchService: GeoSearchService,
        private restService: RestService,
        private moteService: MoteService,
        private cd: ChangeDetectorRef,
        private mapOverlayService: MapOverlayService
    ) { }

    ngOnInit(): void {

        AutoSub(this).reg['activeToolSub'] = this.mapOverlayService.watchActiveTool().subscribe((activeTool) => {
            // Close search if another map-overlay tool is active, or all is inactive
            if(!activeTool || (activeTool != 'map-search-bar' && activeTool != 'geo-search')) this.geoSearch.closeAndClear();
        })

        AutoSub(this).reg['mapEditorEventSub'] = this.mapEditorService.watchMapEditorEvents().subscribe( async (ev) => {
            switch(ev.type) {
                case MapEditorEventType.SET_MAP_SEARCH:
                    if(this.storyRelocated) this.restoreStoryPosition();
                    else  {
                        if(!ev.searchText && !ev.coordinates) await this.geoSearch.closeAndClear();
                        if(ev.openSearchBar) this.geoSearch.performSearch(ev.searchText, ev.coordinates);
                    }
                    break;
                case MapEditorEventType.STORY_DRAG_START:
                    this.restoreStoryPosition(ev.storyKey);
                    this.mapEditorService.removeMarker();
                    break;
                case MapEditorEventType.STORY_DRAG_END:
                    // If a new story has been relocated, update the stored position (for multiple relocations of the same story, we store only the first position)
                    if(this.storyRelocated?.storyKey != ev.storyKey) this.storyRelocated = new MoveStoryEvent(ev.storyKey, ev.oldPosition);
                    this.geoSearch.performSearch(null, ev.newPosition);
                    // Get the story being dragged
                    this.relocatedStory = await this.moteService.getMote(ev.storyKey);
                    // Show street view image if current image is autogenerated (or doesn't contain a image url, yet)
                    this.showStreetViewImage(ev.newPosition, this.relocatedStory?.image);
                    break;
                case MapEditorEventType.MOVE_STORY:
                    this.confirmRelocation();
                    break;
            }
        });
        this.watchMapCenter();
    }

    private async watchMapCenter() {
        // Await map init
        await this.mapService.awaitInit();
        // Watch center to set search proximity coords
        AutoSub(this).reg['mapCenterSub'] = this.mapService.watchMapCenter().subscribe( center => {
            this.searchProximityCoords = center.coords;
        });
    }

    public async resultSelected(result: PlaceResult) {
        this.searchResult = result;
        // If search has been cleared and closed, restore any relocated story and remove any position pin
        if(!this.searchResult) {
            this.restoreStoryPosition();
            this.mapEditorService.removeMarker();
            return;
        }

        // If this is the relocation of an existing story, exit
        if(this.storyRelocated) return;
        // Add position pin to map
        this.mapEditorService.addMarker(result.position);
        // Fit map to show position pin
        this.fitMap(result.position);
        // Show street view blob as image in dom
        this.showStreetViewImage(result.position);
        this.mapOverlayService.updateActiveTool(true, 'map-search-bar');
    }

    private async showStreetViewImage(position: Coords, media?: Media) {
        // Just show normal story image, if story has a manual uploaded (non auto generated) image
        if(media && (media.hd.url && !media.isAutoGenerated)) {
            this.storyImage = media.hd.url;
            this.streetViewImageFile = null;
            return;
        }

        // Reset previous image while loading new
        this.storyImage = '';
        // Get street view image blob and save it for later if needed on create
        this.streetViewImageFile = await this.restService.getStreetViewImage(position.lat, position.long);
        // Show image in dom as image url data
        this.storyImage = await fileToDataUrl(this.streetViewImageFile);
        // We need change detection as the file reader event does not seem to trigger angular
        this.cd.detectChanges();
    }

    private fitMap(position: Coords) {
        // If position is not already visible on map, fly to it
        let visible = this.mapService.isPositionOnMap(position);
        if(!visible) {
            // Padding accounting for editor menu and header
            let padding = { top: 90, bottom: 10, left: 400, right: 10 };
            this.mapService.fitMapBoundsToCoords([position.longLat(), position.longLat()], padding);
        }
    }

    public async addStoryHere() {
        const coords = this.searchResult.position;
        const title = this.searchResult.name;
        const addressInfo = await this.geoSearchService.getAddress(coords);
        const image = this.streetViewImageFile;
        
        // Always save placeId, but flag whether it's an attraction/POI
        const placeId = this.searchResult.placeId || "";
        const placeIdName = this.searchResult.name || "";
        const placeIdIsAttraction = this.geoSearchService.isPOI(this.searchResult);
        const placeIdCheckedOn = Date.now();

        this.geoSearch.closeAndClear();
        this.addStory.emit({ coords, title, addressInfo, image, placeId, placeIdName, placeIdIsAttraction, placeIdCheckedOn });

        this.mapEditorService.removeMarker();
    }

    public confirmRelocation() {
        this.geoSearch.closeAndClear();
        // Upload new street view image file if needed after relocation
        if(this.streetViewImageFile) this.updateStoryImage.emit({ story: this.relocatedStory, image: this.streetViewImageFile });
        // Reset relocated story state
        this.streetViewImageFile = null;
        this.relocatedStory = null;
        this.storyRelocated = null;
    }

    /**
     * Restore position for the currently moved story, if there is one
     */
    public restoreStoryPosition(newStoryRelocated?: string) {
        // If no story has been relocated, do nothing
        if(!this.storyRelocated) return;
        // If the same story has been relocated, do nothing
        if(newStoryRelocated && this.storyRelocated.storyKey == newStoryRelocated) return;
        // Otherwise restore the most recent relocation
        this.mapEditorService.sendMapEditorEvent(this.storyRelocated);
    }

    /**
     * On drawer open/close
     */
    public drawerOpenChanged(open: boolean) {
        // Notify other map-overlay tools whether drawer is open and what content it has (map-search-bar should stay open on map click, but geo-search should close)
        this.mapOverlayService.updateActiveTool(open, this.searchResult ? 'map-search-bar' : 'geo-search');
    }

    ngOnDestroy(): void {
        this.mapEditorService.removeMarker();
    }

}
